import { shallowRef } from 'vue'
import { createSessionMarker } from './sessionMarker'

// Shared state between the DOM-desync reload plugin (writes) and the APM plugin (reads), as in chunkRecovery.ts.

// 10s TTL mirrors reloadNuxtApp's own per-path reload guard.
const marker = createSessionMarker('core:dom-desync-reload', 10_000)

const MISSING_DOM_NODE = /insertBefore|removeChild|The object can not be found here/
const HALF_PATCHED_TREE = /emitsOptions|property 'bum'/

/**
 * Vue patching DOM that something outside Vue changed (page translation wraps text nodes, extensions), and the
 * half-patched component tree that throws on every later update and unmount. The page stays dead until reloaded.
 */
export const isDomDesyncError = (error: unknown): boolean => {
    const { name, message = '' } = (error ?? {}) as { name?: string, message?: string }

    if (name === 'NotFoundError') return MISSING_DOM_NODE.test(message)

    return name === 'TypeError' && HALF_PATCHED_TREE.test(message)
}

const reloadTriggered = shallowRef(false)
const depositStarted = shallowRef(false)

/** A deposit started or moved on: the DOM-desync reload stays off for the rest of this page load, so it cannot cut a payment short. */
export const markDepositStarted = (): void => {
    depositStarted.value = true
}

export const hasDepositStarted = (): boolean => depositStarted.value

/** Record a DOM-desync recovery reload. The guarded follow-up errors of the same burst count as answered by it. */
export const markDomDesyncReload = (): void => {
    reloadTriggered.value = true
    marker.mark()
}

/** Read and clear the marker set before a recovery reload; true when this page load is a fresh one. */
export const consumeDomDesyncReloadMarker = (): boolean => marker.consume()

/** Google Translate classes `<html>`; translators also rewrite its `lang`, so compare with the server-rendered one. */
export const isPageTranslated = (servedLang: string): boolean => {
    const html = document.documentElement

    return /\btranslated-(?:ltr|rtl)\b/.test(html.className) || html.lang !== servedLang
}

interface ApmErrorEvent {
    exception?: {
        message?: string
        type?: string
    }
    context?: {
        tags?: Record<string, unknown>
    }
}

interface ApmPayload {
    errors?: ApmErrorEvent[]
}

/** APM filter: label errors with page translation and browser language, and desync errors with whether a reload answered them. */
export const labelDomDesyncErrors = <T extends ApmPayload>(payload: T, servedLang: string): T => {
    const page = { page_translated: isPageTranslated(servedLang), browser_language: navigator.language }

    payload.errors?.forEach((error) => {
        const desync = isDomDesyncError({ name: error.exception?.type, message: error.exception?.message })
        error.context = { ...error.context, tags: { ...error.context?.tags, ...page, ...(desync && { dom_desync_reloaded: reloadTriggered.value }) } }
    })

    return payload
}
